import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { TitleWithActions, AdminEmptyState } from '@/components';
import { EmptyStateType } from './AdminEmptyState';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

type ButtonType = 'add' | 'download';

interface AdminEmptyScreenLayoutProps {
  title: string;
  emptyStateType: EmptyStateType;
  onAddPress?: () => void;
  showAddButton?: boolean;
  buttonType?: ButtonType;
  children?: React.ReactNode;
  hasContent?: boolean;
  disableScrollView?: boolean;
  /** When false, suppress the internal title bar — the screen renders its own header. */
  showTitleBar?: boolean;
}

export const AdminEmptyScreenLayout: React.FC<AdminEmptyScreenLayoutProps> = ({
  title,
  emptyStateType,
  onAddPress,
  showAddButton = true,
  buttonType = 'add',
  children,
  hasContent = false,
  disableScrollView = false,
  showTitleBar = true,
}) => {
  const styles = useStyles();
  const insets = useSafeAreaInsets();

  return (
    <View style={styles.contentArea}>
      <View style={[styles.surface, showTitleBar ? null : styles.surfaceNoTitleBar]}>
        {showTitleBar && (
          <View style={styles.titleArea}>
            <TitleWithActions
              title={title}
              showAddButton={showAddButton}
              showSelector={false}
              showControlBar={false}
              onAddPress={onAddPress}
              buttonType={buttonType}
            />
          </View>
        )}

        <View style={styles.listContainer}>
          {disableScrollView ? (
            <View style={[styles.scrollView, hasContent ? styles.scrollContentWithData : styles.scrollContent]}>
              {children ? (
                children
              ) : (
                <View style={styles.emptyStateContainer}>
                  <AdminEmptyState type={emptyStateType} />
                </View>
              )}
            </View>
          ) : (
            <ScrollView
              style={styles.scrollView}
              contentContainerStyle={hasContent ? styles.scrollContentWithData : styles.scrollContent}
              showsVerticalScrollIndicator={false}
              scrollEnabled={children ? true : false}
            >
              {children ? (
                children
              ) : (
                <View style={styles.emptyStateContainer}>
                  <AdminEmptyState type={emptyStateType} />
                </View>
              )}
            </ScrollView>
          )}
        </View>
      </View>

      <View style={{ height: insets.bottom }} />
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(() => {
    return StyleSheet.create({
      contentArea: {
        flex: 1,
        backgroundColor: 'transparent',
      },
      surface: {
        flex: 1,
        width: '100%',
        gap: 20,
        paddingTop: 20,
        backgroundColor: 'transparent',
      },
      surfaceNoTitleBar: {
        paddingTop: 0,
        gap: 0,
      },
      titleArea: {
        paddingHorizontal: 16,
      },
      listContainer: {
        width: '100%',
        flex: 1,
        paddingHorizontal: 16,
      },
      scrollView: {
        flex: 1,
      },
      scrollContent: {
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 0,
        marginTop: -50,
      },
      scrollContentWithData: {
        flexGrow: 1,
        paddingTop: 0,
        paddingBottom: 0,
      },
      emptyStateContainer: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    });
  });
